import { useEffect, useState } from "react";

import AdminLayout from "../../../layouts/AdminLayout";
import DataTableLayout from "../../../layouts/DataTableLayout";
import DataTable from "../../../components/common/DataTable";

import { Button, Modal, SearchInput, LoadingSkeleton } from "../../../components/ui";
import SocietyAdminForm from "../../../components/societies/SocietyAdminForm";
import { Plus, Eye, Pencil, Trash2 } from "lucide-react";

import {
  getSocietyAdmins,
  deleteSocietyAdmin,
} from "../../../services/SocietyAdminServices";
import { getSocieties } from "../../../services/societyService";

const SocietyAdmin = () => {
  const [societies, setSocieties] = useState([]);
  const [availableSocieties, setAvailableSocieties] = useState([]);
  const [loading, setLoading] = useState(true);

  const [openModal, setOpenModal] = useState(false);
  const [selected, setSelected] = useState(null);
  const [viewMode, setViewMode] = useState(false);

  const [search, setSearch] = useState("");

  // 🔹 FETCH DATA
  const fetchSocieties = async () => {
    try {
      const data = await getSocietyAdmins();
      setSocieties(data);
    } catch (err) {
      console.error("Error fetching societies:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableSocieties = async () => {
    try {
      const data = await getSocieties();
      setAvailableSocieties(data);
    } catch (err) {
      console.error("Error fetching available societies:", err);
    }
  };

  useEffect(() => {
    fetchSocieties();
    fetchAvailableSocieties();
  }, []);

  // 🔹 FILTER LOGIC
  const filteredData = societies.filter((item) => {
    const q = search.toLowerCase();
    return (
      !q ||
      item.society_admin_name?.toLowerCase().includes(q) ||
      item.society_name?.toLowerCase().includes(q)
    );
  });

  // 🔹 HANDLERS
  const handleAdd = () => {
    setSelected(null);
    setViewMode(false);
    setOpenModal(true);
  };

  const handleEdit = (row) => {
    setSelected(row);
    setViewMode(false);
    setOpenModal(true);
  };

  const handleView = (row) => {
    setSelected(row);
    setViewMode(true);
    setOpenModal(true);
  };

  const handleDelete = async (row) => {
    if (!window.confirm("Deactivate this society admin?")) return;

    try {
      await deleteSocietyAdmin(row.user_id);
      fetchSocieties();
    } catch (err) {
      console.error(err);
    }
  };

  // 🔹 COLUMNS
  const columns = [
    {
      header: "Sr No",
      render: (_, index) => index + 1,
    },
    { header: "Society Admin Name", accessor: "society_admin_name" },
    { header: "Society Name", accessor: "society_name" },
    { header: "Email", accessor: "email" },
    { header: "Phone", accessor: "phone" },
    {
      header: "Action",
      render: (row) => (
        <div style={{ display: "inline-flex", gap: "6px" }}>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleView(row)}
            title="View Society Admin"
            icon={<Eye size={14} />}
          />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleEdit(row)}
            title="Edit Society Admin"
            icon={<Pencil size={14} />}
          />
          <Button
            variant="danger"
            size="sm"
            onClick={() => handleDelete(row)}
            title="Deactivate Society Admin"
            icon={<Trash2 size={14} />}
          />
        </div>
      ),
    },
  ];

  return (
    <AdminLayout title="Society Administrators">
      <DataTableLayout
        title="Society Admin Management"
        filters={
          <div style={{ minWidth: "260px" }}>
            <SearchInput
              placeholder="Search society admin or society..."
              value={search}
              onChange={(val) => setSearch(val)}
            />
          </div>
        }
        actions={
          <Button variant="primary" onClick={handleAdd} icon={<Plus size={16} />}>
            Add Society Admin
          </Button>
        }
      >
        {loading ? (
          <div style={{ padding: "24px" }}>
            <LoadingSkeleton count={4} height="48px" style={{ marginBottom: "8px" }} />
          </div>
        ) : (
          <DataTable columns={columns} data={filteredData} />
        )}
      </DataTableLayout>

      {/* 🔥 MODAL */}
      <Modal
        isOpen={openModal}
        onClose={() => setOpenModal(false)}
        title={
          viewMode
            ? "View Society Admin"
            : selected
            ? "Edit Society Admin"
            : "Add Society Admin"
        }
      >
        <SocietyAdminForm
          society={selected}
          societies={availableSocieties}
          readOnly={viewMode}
          onSuccess={() => {
            setOpenModal(false);
            fetchSocieties(); // refresh
          }}
        />
      </Modal>
    </AdminLayout>
  );
};

export default SocietyAdmin;
